<script setup>
/**
 * ContactPreviewCard
 *
 * Step 2 of `NewWhatsAppChatFlow` (and, in `compact` mode, the recap header
 * shown above the template picker in step 3). Shows who we're about to
 * message and whether they're actually reachable on a WhatsApp inbox -
 * a contact can exist without one (e.g. an email-only contact), and that
 * must block sending rather than fail silently later.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contact: { type: Object, required: true },
  whatsappInbox: { type: Object, default: null },
  isFetchingInboxes: { type: Boolean, default: false },
  isExistingContact: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

defineEmits(['back', 'continue']);

const { t } = useI18n();
const i18nPrefix = 'NEW_WHATSAPP_CHAT.PREVIEW_STEP';

const subtitle = computed(
  () => props.contact.phoneNumber || props.contact.email || ''
);

const canContinue = computed(
  () => !props.isFetchingInboxes && !!props.whatsappInbox
);
</script>

<template>
  <div v-if="compact" class="flex items-center gap-2 px-4 pt-4">
    <Avatar :name="contact.name" :src="contact.thumbnail" :size="24" />
    <span class="text-sm truncate text-n-slate-12">{{ contact.name }}</span>
    <span class="text-xs truncate text-n-slate-11">{{ subtitle }}</span>
  </div>

  <div v-else class="flex flex-col gap-4 px-4 pb-4">
    <div class="flex flex-col items-center gap-2 py-2">
      <Avatar :name="contact.name" :src="contact.thumbnail" :size="56" />
      <div class="flex flex-col items-center gap-0.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ contact.name }}
        </span>
        <span class="text-xs text-n-slate-11">{{ subtitle }}</span>
      </div>
      <span
        class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
      >
        {{
          isExistingContact
            ? t(`${i18nPrefix}.EXISTING_CONTACT_BADGE`)
            : t(`${i18nPrefix}.NEW_CONTACT_BADGE`)
        }}
      </span>
    </div>

    <div
      class="flex items-center gap-2 p-2 text-xs rounded-lg bg-n-alpha-1"
      :class="{
        'text-n-ruby-9': !isFetchingInboxes && !whatsappInbox,
        'text-n-slate-11': isFetchingInboxes || whatsappInbox,
      }"
    >
      <Spinner v-if="isFetchingInboxes" :size="14" />
      <Icon
        v-else
        :icon="
          whatsappInbox ? 'i-lucide-circle-check' : 'i-lucide-circle-alert'
        "
        class="size-3.5 shrink-0"
      />
      <span v-if="isFetchingInboxes">
        {{ t(`${i18nPrefix}.CHECKING_WHATSAPP`) }}
      </span>
      <span v-else-if="whatsappInbox">
        {{
          t(`${i18nPrefix}.REACHABLE_VIA`, { inboxName: whatsappInbox.name })
        }}
      </span>
      <span v-else>{{ t(`${i18nPrefix}.NOT_REACHABLE`) }}</span>
    </div>

    <div class="flex gap-2">
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        class="flex-1"
        :label="t(`${i18nPrefix}.BACK`)"
        @click="$emit('back')"
      />
      <Button
        size="sm"
        class="flex-1"
        :disabled="!canContinue"
        :label="t(`${i18nPrefix}.CONTINUE`)"
        @click="$emit('continue')"
      />
    </div>
  </div>
</template>
